import { useEffect, useMemo, useState } from "react";
import { Modal } from "./Modal";
import { FormQuestion } from "./FormQuestion";
import { api, ApiError } from "../lib/api";
import { useResource } from "../lib/hooks";
import {
    applicableAnswers, isAnswered, unanswered, visiblePages,
    type Answers, type FormModel,
} from "../lib/formModel";

/**
 * The wizard that draws TrustCare's quality forms.
 *
 * It holds no questions of its own. `GET /forms/:name` returns the same model
 * the API validates submissions against, so this screen asks exactly what the
 * register accepts — 239 questions on the incident report, with the wording,
 * choices and show/hide rules the form already decided. Nothing here needs
 * changing when a form does.
 *
 * Pages appear and disappear as the answers change: a fall never shows the
 * property damage pages, because the form says those apply to property damage.
 * That is the form's own rule, applied here and again on the server.
 */

/**
 * A question whose answer fetches a linked record - the investigation's
 * incident reference. When the named question is answered, `fetch` is called
 * with the answer; what it returns is merged into the answers (prefilling the
 * questions the linked record can already answer), and the reporter is told
 * what was linked, or that nothing was.
 */
export interface Linker {
    field: string;
    fetch: (value: string) => Promise<{ label: string; answers: Answers } | null>;
}

interface Props {
    /** Form to draw, e.g. "incident-report". */
    formName: string;
    title: string;
    subtitle?: string;
    /** POST target for the finished submission, e.g. "/incident-reports". */
    path: string;
    /** Sent alongside the answers, e.g. the organisation a staff member files for. */
    extra?: Record<string, unknown>;
    /** Offer "save and finish later". Only where the register has a draft state. */
    allowDraft?: boolean;
    linker?: Linker;
    onClose: () => void;
    onSaved: () => void;
}

export const FormWizard = ({
    formName, title, subtitle, path, extra, allowDraft = false, linker, onClose, onSaved,
}: Props) => {
    const form = useResource<FormModel>(`/forms/${formName}`);
    const [answers, setAnswers] = useState<Answers>({});
    const [pageIndex, setPageIndex] = useState(0);
    const [showGaps, setShowGaps] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** The question the server named, so the reporter is sent to it rather than told "invalid". */
    const [rejected, setRejected] = useState<string | null>(null);

    /** What the linker last resolved: linked, not found, or nothing yet. */
    const [linked, setLinked] = useState<{ value: string; label: string | null } | null>(null);

    const model = form.data;
    const pages = useMemo(() => (model ? visiblePages(model, answers) : []), [model, answers]);

    // When the linking question is answered, look the record up and carry its
    // details across, so the investigator is not retyping what the register
    // already knows. Debounced: a reference is typed character by character.
    const linkValue = linker ? String(answers[linker.field] ?? "").trim() : "";
    useEffect(() => {
        if (!linker) return;
        if (!linkValue) { setLinked(null); return; }
        let stale = false;
        const timer = setTimeout(() => {
            linker.fetch(linkValue)
                .then((found) => {
                    if (stale) return;
                    setLinked({ value: linkValue, label: found?.label ?? null });
                    if (found) setAnswers((previous) => ({ ...found.answers, ...previous }));
                })
                .catch(() => { if (!stale) setLinked({ value: linkValue, label: null }); });
        }, 500);
        return () => { stale = true; clearTimeout(timer); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [linkValue]);

    // Answering a question can close the page you are standing on — choosing
    // "medication" removes the property damage pages. Stay in range rather than
    // showing nothing.
    useEffect(() => {
        if (pages.length > 0 && pageIndex >= pages.length) setPageIndex(pages.length - 1);
    }, [pages.length, pageIndex]);

    const page = pages[pageIndex];
    const gaps = page ? unanswered(page.fields, answers) : [];

    const set = (key: string, value: unknown) => {
        setAnswers((previous) => ({ ...previous, [key]: value }));
        if (rejected === key) setRejected(null);
    };

    const goTo = (index: number) => {
        setShowGaps(false);
        setPageIndex(Math.max(0, Math.min(index, pages.length - 1)));
    };

    /** Send the reporter to the page holding a question, by its key. */
    const jumpToField = (key: string) => {
        const index = pages.findIndex((p) => p.fields.some((f) => f.key === key));
        if (index >= 0) {
            setPageIndex(index);
            setShowGaps(true);
        }
        setRejected(key);
    };

    const submit = async (saveAsDraft: boolean) => {
        if (!model) return;
        setSaving(true);
        setError(null);
        setRejected(null);
        try {
            await api.post(path, {
                ...extra,
                details: applicableAnswers(model, answers),
                saveAsDraft,
            });
            onSaved();
        } catch (err) {
            if (err instanceof ApiError) {
                setError(err.message);
                const field = (err.body as { field?: string } | null)?.field;
                if (field) jumpToField(field);
            } else {
                setError(err instanceof Error ? err.message : "Could not file this report");
            }
        } finally {
            setSaving(false);
        }
    };

    const answeredCount = Object.values(answers).filter(isAnswered).length;
    const last = pageIndex === pages.length - 1;

    return (
        <Modal
            title={title}
            subtitle={subtitle}
            onClose={onClose}
            width="wide"
            footer={
                <>
                    <button className="btn ghost" onClick={() => goTo(pageIndex - 1)}
                        disabled={saving || pageIndex === 0}>
                        Back
                    </button>
                    {!last && (
                        <button className="btn" onClick={() => goTo(pageIndex + 1)} disabled={saving}>
                            Next
                        </button>
                    )}
                    <button className="btn" onClick={() => submit(false)} disabled={saving || !model}>
                        {saving ? "Filing…" : "File this report"}
                    </button>
                    {allowDraft && (
                        <button className="btn ghost" onClick={() => submit(true)} disabled={saving || !model}>
                            Save and finish later
                        </button>
                    )}
                    <button className="btn ghost" onClick={onClose} disabled={saving}>Cancel</button>
                </>
            }
        >
            {form.loading && <p className="empty">Loading the form…</p>}
            {form.error && <div className="error-banner">{form.error}</div>}

            {model && page && (
                <div className="wizard">
                    <nav className="wizard-nav" aria-label="Sections">
                        {pages.map((p, index) => {
                            const missing = unanswered(p.fields, answers).length;
                            return (
                                <button
                                    key={p.title}
                                    type="button"
                                    className={`wizard-step ${index === pageIndex ? "current" : ""}`}
                                    aria-current={index === pageIndex}
                                    onClick={() => goTo(index)}
                                >
                                    <span className="wizard-step-title">{index + 1}. {p.title}</span>
                                    {missing > 0
                                        ? <span className="chip warn">{missing}</span>
                                        : <span className="chip pass">✓</span>}
                                </button>
                            );
                        })}
                    </nav>

                    <section className="wizard-body">
                        {error && <div className="error-banner">{error}</div>}
                        {linker && linked && (
                            linked.label
                                ? <div className="wizard-linked pass">
                                      Linked to <b>{linked.value}</b> — {linked.label}. Its details have been carried across.
                                  </div>
                                : <div className="wizard-linked warn">
                                      No record found for <b>{linked.value}</b> in your organisation. Check the reference — or tick “Enter details manually” to record it by hand.
                                  </div>
                        )}

                        <div className="wizard-page-head">
                            <h3>{page.title}</h3>
                            <span className="chip mute">
                                Page {pageIndex + 1} of {pages.length} · {answeredCount} answered
                            </span>
                        </div>

                        {showGaps && gaps.length > 0 && (
                            <div className="error-banner">
                                Still to answer on this page: {gaps.map((f) => f.label || f.key).join(", ")}
                            </div>
                        )}

                        <div className="form-grid">
                            {page.fields.map((field) => (
                                <FormQuestion
                                    key={field.key}
                                    field={field}
                                    value={answers[field.key]}
                                    invalid={rejected === field.key || (showGaps && gaps.includes(field))}
                                    onChange={(value) => set(field.key, value)}
                                />
                            ))}
                        </div>

                        <p className="help" style={{ marginTop: "0.8rem" }}>
                            Questions appear as they become relevant. Nothing is filed until you
                            choose to file it{allowDraft ? ", and an unfinished report can be saved and picked up later" : ""}.
                        </p>
                    </section>
                </div>
            )}
        </Modal>
    );
};
